import { Autocomplete, FormControl, FormLabel } from '@mui/joy';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CaseType } from '~/common/enums';

import { type RouterOutputs, trpc } from '@/config/trpc';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';
import { getOpenCaseInNewTabValue } from '@/shared/utils/helpers';
import useGetCaseTemplate from '@/queries/cases/useGetCaseTemplate';

import AddCaseActions from './AddCaseActions';
import CommonFormBody from './CommonFormBody';
import type { DefaultFormValues } from './Config/FormValues';
import { InlineSpinner } from '@/shared/icons/Icons';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';
import TemplateAlerts from './TemplateAlerts';
import useAddCaseFormConfig from './Config/useAddCaseFormConfig';

type CaseTemplateOption = RouterOutputs['team']['case']['template']['autocomplete'][0];

type Props = {
  toggle: UseToggle<unknown>;
  callback?: (res: string) => void;
  initialData?: DefaultFormValues;
};

const AddCase: React.FC<Props> = ({ callback, toggle, initialData }) => {
  const { t } = useTranslation();

  const { data: settings } =
    trpc.messaging.pushNotificationSettings.getBuildingPortalPushNotificationSettings.useQuery();

  const [selectedTemplate, setSelectedTemplate] = useState<CaseTemplateOption | null>(null);

  const toggles = {
    openCaseInNewTab: useToggle(getOpenCaseInNewTabValue()),
    pushNotification: useToggle(settings?.caseAdded),
    redirect: useToggle(!getOpenCaseInNewTabValue()),
    showOnPortal: useToggle(),
    task: useToggle(),
  };

  const { data: caseTemplates, isLoading: isLoadingCaseTemplates } =
    trpc.team.case.template.autocomplete.useQuery({
      isManual: true,
    });

  const { currentCaseTemplate, currentCaseTemplateIsFetching } = useGetCaseTemplate(
    selectedTemplate?.value,
  );

  const { initialValues, validationSchema, handleSubmit, isCreatingCase } = useAddCaseFormConfig({
    callback,
    caseType: CaseType.Case,
    initialData,
    templateId: selectedTemplate?.value,
    toggle,
    toggles,
  });

  return (
    <KpcModal
      className="max-h-[80vh] w-4/5 overflow-auto 2xl:w-2/3"
      title={t('createCase')}
      toggle={toggle}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        <Form className="flex flex-col space-y-4">
          <FormControl>
            <FormLabel>{t('masterCaseTemplate')}</FormLabel>
            <Autocomplete
              loading={isLoadingCaseTemplates}
              endDecorator={currentCaseTemplateIsFetching && <InlineSpinner />}
              placeholder={t('search.placeholder')}
              options={caseTemplates ?? []}
              isOptionEqualToValue={(option, selectedOption) =>
                option.value === selectedOption.value
              }
              onChange={(_e, option) => {
                if (option) {
                  setSelectedTemplate(option);
                } else {
                  setSelectedTemplate(null);
                }
              }}
            />
          </FormControl>
          <CommonFormBody
            taskToggle={toggles.task}
            caseType={CaseType.Case}
            currentCaseTemplate={currentCaseTemplate}
          />

          <TemplateAlerts template={currentCaseTemplate} toggle={toggle} />

          <AddCaseActions
            toggles={{
              modal: toggle,
              ...toggles,
            }}
            isMutating={isCreatingCase}
          />
        </Form>
      </Formik>
    </KpcModal>
  );
};

export default AddCase;
